import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AreaRail, AreaRailSkeleton } from "@/components/chrome/AreaRail";
import { TopBar } from "@/components/chrome/TopBar";
import { ViewAsBanner } from "@/components/chrome/ViewAsBanner";
import { exitViewAs, rereadAfterViewAsChange, viewAsFromMe } from "@/lib/viewAs";
import {
  initialRailCollapsed,
  isRailToggleChord,
  NARROW_VIEWPORT,
  writeRailCollapsed,
} from "@/lib/railCollapse";
import { DockedModule, ModuleViewport } from "@/components/shell";
import { defaultTabKey, resolveActiveTabKey } from "@/lib/tabs";
import { carryFilters } from "@/lib/filters";
import { effectiveDecision } from "@/lib/editLockBypass";
import { resolveNativeModule, useNativeModuleMeta, useNativeModuleTabHints } from "@/modules/registry";
import {
  needsSignIn,
  ownerDisplayName,
  probeMe,
  redirectToSignIn,
  resolveLandingState,
  resolveOpenArea,
  resolveShellState,
} from "@/lib/shell";

/**
 * The content area's gutter.
 *
 * The top step is deliberately smaller than the sides and bottom: the top bar
 * already separates the content from the window, so a full 32px on top of it
 * read as a dead band above every module — most visibly above a DOCKED
 * surface, which then renders its own header inside the frame (Damien,
 * 2026-08-26). Still the shell's own scale, one step tighter.
 *
 * One constant, used by BOTH the loading skeleton and the resolved shell: a
 * skeleton laid out on a different gutter reflows the moment content arrives,
 * which is the jump the reserved-dimensions rule exists to prevent.
 */
const MAIN_GUTTER = "flex min-h-0 flex-1 flex-col overflow-y-auto px-8 pt-4 pb-8";

/**
 * A DOCKED module gets no gutter at all.
 *
 * The gutter is for content the shell lays out. A docked surface is a whole
 * product with its own margins, header and background, so wrapping it in 32px
 * of shell padding and a rounded border drew a box-in-a-box and spent the
 * room the surface needed — "why don't the modules take up 100% of the space,
 * and you remove this weird in frame approach" (Damien, 2026-08-27). Flush is
 * the honest rendering: the shell owns the chrome ABOVE the surface (top bar,
 * rail, tab bar) and nothing around it.
 */
const MAIN_FLUSH = "flex min-h-0 flex-1 flex-col overflow-hidden";

/**
 * The shell is exactly the viewport tall, and the SCROLL lives inside it.
 *
 * `min-h-dvh` let the page grow past the window, so a docked frame pinned at a
 * fixed height sat in a page that scrolled around it — two scrollbars, and a
 * frame that could never use the room below it (Damien, 2026-08-27). `h-dvh`
 * plus `overflow-hidden` makes the window the outer bound; `min-h-0` on each
 * flex descendant is what actually lets an inner region shrink and scroll,
 * because a flex item's default `min-height: auto` refuses to go below its
 * content and silently defeats the whole chain.
 */
const SHELL_FRAME = "flex h-dvh flex-col overflow-hidden";

/**
 * S2 shell + S3 landing area.
 *
 * The rule that shapes every branch below: **the shell renders, and no module
 * opens, when we cannot confirm access** (H-10, BR-3). An error state is a
 * shell with a stated reason — never a blank screen, and never an empty rail,
 * which would tell the person something false about their own access.
 */
export function ShellPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as {
    area?: string;
    module?: string;
    tab?: string;
  };

  // `?at=` — a deep path INSIDE a docked surface, set by LegacyRedirectPage
  // when an old subdomain deep link named something a portal route cannot say
  // (which artefact, which record). Read off the raw search string rather than
  // a route schema, so no route needs to declare it and a module that never
  // docks is unaffected. `resolveDock` re-checks it against the surface origin
  // and ignores anything that would leave it.
  const location = useLocation();
  const at = new URLSearchParams((location.searchStr ?? "").replace(/^\?/, "")).get("at");

  // `/app/home` — the neutral screen the logo resets to. Nothing is open and
  // nothing loads: the person picks where to go from the rail.
  const isHome = location.pathname.replace(/\/+$/, "") === "/app/home";

  const queryClient = useQueryClient();
  const probe = useQuery({
    queryKey: ["me"],
    queryFn: ({ signal }) => probeMe(signal),
    retry: false,
  });

  const state = resolveShellState(probe.data, probe.isPending);

  // A lapsed sign-in is not an outage: send the browser through Access and back
  // to this exact URL, once. If that already happened moments ago and the probe
  // still refuses, stop and offer a button rather than loop.
  const signInNeeded =
    state === "error" && needsSignIn(probe.data?.kind === "failed" ? probe.data.failure : null);
  const [signInStalled, setSignInStalled] = useState(false);
  useEffect(() => {
    if (!signInNeeded) return;
    if (!redirectToSignIn(() => window.location.reload(), attemptStore())) setSignInStalled(true);
  }, [signInNeeded]);

  const meNow = probe.data?.kind === "me" ? probe.data.me : null;
  const viewAs = viewAsFromMe(meNow ?? undefined);

  // Computed here, ahead of every early `return` below, so the native-module
  // meta hook is called on EVERY render in the SAME order (rules of hooks) —
  // `null` before the probe resolves or when the open module is docked/absent,
  // same as the "real" openArea/openModule computed further down for render.
  const areaKeyEarly =
    meNow && !isHome ? resolveOpenArea(meNow, params.area ?? null).areaKey : null;
  const openAreaEarly = meNow?.areas.find((a) => a.area_key === areaKeyEarly) ?? null;
  const openModuleEarly =
    openAreaEarly?.modules.find((m) => m.module_key === params.module) ??
    openAreaEarly?.modules[0] ??
    null;
  const nativeModuleKey =
    openModuleEarly && openModuleEarly.kind !== "docked" ? openModuleEarly.module_key : null;
  // The RESOLVED tab, never the raw `params.tab`: that can name a tab the person
  // does not hold, or a typo, while the module renders its fallback.
  const tabHints = useNativeModuleTabHints(nativeModuleKey);
  const nativeMeta = useNativeModuleMeta(
    nativeModuleKey,
    openModuleEarly ? resolveActiveTabKey(openModuleEarly.tabs, params.tab, tabHints?.preferred) : null,
  );

  /**
   * The rail collapses so a docked surface can have the width.
   *
   * State lives here rather than in the rail, because collapsing is not
   * something the rail does to itself — it changes the shell's layout, and both
   * the toggle (in the top bar) and the main gutter read it. Seeded from the
   * viewer's last choice so the preference survives a reload.
   */
  const [railCollapsed, setRailCollapsed] = useState(initialRailCollapsed);
  const toggleRail = () =>
    setRailCollapsed((prev) => {
      writeRailCollapsed(!prev);
      return !prev;
    });

  // ⌘\ / Ctrl+\ — the chord people already know from Notion. Bound on the
  // window so it works while focus is inside a docked frame's sibling chrome;
  // it cannot reach INSIDE a cross-origin frame, which is why the button
  // exists and is not merely a shortcut hint.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isRailToggleChord(e)) return;
      e.preventDefault();
      setRailCollapsed((prev) => {
        writeRailCollapsed(!prev);
        return !prev;
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Arriving home always opens the rail: the home screen tells the person to
  // pick from the menu, so a remembered "collapsed" would hide the only thing
  // they can act on. Not written to storage — the stored choice still applies
  // once they toggle it themselves.
  useEffect(() => {
    if (isHome) setRailCollapsed(false);
  }, [isHome]);

  /**
   * Crossing INTO a narrow viewport collapses the rail; crossing back out does
   * not re-open it.
   *
   * One-directional on purpose. Auto-collapsing rescues a window that has
   * become too narrow to work in, but auto-expanding would overrule someone
   * who deliberately collapsed the rail on a wide screen — and the whole point
   * of the toggle is that the choice is theirs. Nothing is written to storage
   * here either: this is the shell reacting to the window, not the person
   * making a decision.
   */
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(NARROW_VIEWPORT);
    } catch {
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setRailCollapsed(true);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const leaveViewAs = async () => {
    await exitViewAs();
    // Re-read rather than clear a local flag: the server owns the mode, so the
    // only honest way back is to ask it again — past the browser's cached copy.
    await rereadAfterViewAsChange(queryClient);
  };

  if (state === "loading") {
    return (
      <div className={SHELL_FRAME}>
        <TopBar me={null} railCollapsed={railCollapsed} onToggleRail={toggleRail} />
        <div className="flex min-h-0 flex-1">
          {!railCollapsed && <AreaRailSkeleton />}
          <main className={MAIN_GUTTER}>
            {/* Reserved at final dimensions so nothing reflows when it fills. */}
            <Skeleton className="mb-3 h-7 w-64" />
            <Skeleton className="h-4 w-80" />
          </main>
        </div>
      </div>
    );
  }

  if (state === "error" && signInNeeded) {
    return (
      <div className={SHELL_FRAME}>
        <TopBar me={null} />
        <main className={MAIN_GUTTER}>
          {signInStalled ? (
            <>
              <Alert className="max-w-2xl">
                <AlertTitle>Your sign-in has expired</AlertTitle>
                <AlertDescription>
                  Sign in with Ketosource to carry on where you were.
                </AlertDescription>
              </Alert>
              <Button
                className="mt-6"
                onClick={() =>
                  void redirectToSignIn(() => window.location.reload(), attemptStore(), Date.now(), true)
                }
              >
                Sign in again
              </Button>
            </>
          ) : (
            <p className="text-sm text-ks-ink">Taking you to sign in&hellip;</p>
          )}
        </main>
      </div>
    );
  }

  if (state === "error") {
    const failure = probe.data?.kind === "failed" ? probe.data.failure : null;
    return (
      <div className={SHELL_FRAME}>
        <TopBar me={null} />
        <div className="flex min-h-0 flex-1">
          {/* No rail: an empty rail would assert "you have no areas". We do
              not know that — we know we could not read them. */}
          <main className={MAIN_GUTTER}>
            <Alert variant="destructive" className="max-w-2xl">
              <AlertTitle>We can&rsquo;t confirm your access right now</AlertTitle>
              <AlertDescription>
                {failure?.message ?? "Nothing has been opened."} Damien is the contact while
                it&rsquo;s down.
              </AlertDescription>
            </Alert>
            <Button className="mt-6" onClick={() => void probe.refetch()}>
              Try again
            </Button>
          </main>
        </div>
      </div>
    );
  }

  // From here the probe succeeded, so `me` is present.
  const me = probe.data?.kind === "me" ? probe.data.me : null;
  if (!me) return null;

  if (state === "empty") {
    return (
      <div className={SHELL_FRAME}>
        {/* The banner stays even here: "the target has no entitlements" is
            exactly the finding the owner needed (UX S7 error). */}
        <ViewAsBanner view={viewAs} onExit={() => void leaveViewAs()} />
        <TopBar me={me} canAdminister={me.can_administer ?? false} />
        <main className={MAIN_GUTTER}>
          <Alert className="max-w-2xl">
            <AlertTitle>You don&rsquo;t have access to any area yet.</AlertTitle>
            <AlertDescription>Damien can set this up.</AlertDescription>
          </Alert>
        </main>
      </div>
    );
  }

  const { areaKey, deniedRequest } = isHome
    ? { areaKey: null, deniedRequest: false }
    : resolveOpenArea(me, params.area ?? null);
  const openArea = me.areas.find((a) => a.area_key === areaKey) ?? null;
  const landingState = resolveLandingState(me, areaKey);

  // The landing area opens with its FIRST module already loaded — click 0 in
  // the UX flow. Falling back rather than showing an area-level index page is
  // what keeps the daily path at two clicks warm (UX v0.3 §4).
  const openModule =
    openArea?.modules.find((m) => m.module_key === params.module) ?? openArea?.modules[0] ?? null;

  // Is a DOCKED surface what this screen renders? Drives the flush layout.
  const docked = landingState === "ideal" && !!openArea && openModule?.kind === "docked";

  // A native module renders only a tab the person holds: a URL naming a tab their
  // `/v1/me` left out opens the default tab instead of content their API refuses
  // (bug 3f80402f). A docked surface keeps the URL's tab as its own path, so the
  // DockedModule below still reads `params.tab` directly.
  const activeTabKey = !openModule
    ? null
    : openModule.kind === "docked"
      ? (params.tab ?? defaultTabKey(openModule.tabs))
      : resolveActiveTabKey(openModule.tabs, params.tab, tabHints?.preferred);

  const openAreaRoute = (key: string) => void navigate({ to: "/app/$area", params: { area: key } });
  const openModuleRoute = (key: string, moduleKey: string) =>
    void navigate({
      to: "/app/$area/$module",
      params: { area: key, module: moduleKey },
    });

  return (
    <div className={SHELL_FRAME}>
      {/* Above the product chrome, deliberately: forgetting the mode is the
          only real failure here (UX W5). */}
      <ViewAsBanner view={viewAs} onExit={() => void leaveViewAs()} />
      <TopBar
        me={me}
        canAdminister={me.can_administer ?? false}
        railCollapsed={railCollapsed}
        onToggleRail={toggleRail}
      />
      <div className="flex min-h-0 flex-1">
        {/* A single-area person still gets a rail, expanded on their one area
            (UX S2 edge) — the shell does not change shape per person.
            Collapsing is the one exception, and it is the VIEWER's choice,
            not a shape the shell picks for them. */}
        {!railCollapsed && (
          <AreaRail
            areas={me.areas}
            openAreaKey={areaKey}
            activeModuleKey={params.module ?? null}
            onOpenArea={openAreaRoute}
            onOpenModule={openModuleRoute}
          />
        )}

        {/* Flush ONLY when a docked surface is what fills it. The denial alert
            and the empty-area sentence are the shell's own copy and still need
            the gutter, so the flush branch is gated on there being a docked
            module to render — not merely on the area being open. */}
        <main className={docked ? MAIN_FLUSH : MAIN_GUTTER}>
          {deniedRequest && (
            <Alert className="mb-6 max-w-2xl">
              <AlertTitle>You don&rsquo;t have access to that area.</AlertTitle>
              <AlertDescription>Damien can grant it.</AlertDescription>
            </Alert>
          )}

          {/* The area is named here only when no module is open. Once one is,
              the viewport's own title is the page heading and the rail carries
              the area — two <h1>s would leave "where am I" ambiguous. */}
          {openArea && !openModule && (
            <h1 className="mb-2 text-2xl font-bold text-ks-ink">{openArea.display_name}</h1>
          )}

          {isHome && (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-lg text-ks-ink">
                Select the item you want from the menu on the left.
              </p>
            </div>
          )}

          {landingState === "empty-area" && openArea && (
            <p className="text-ks-ink">
              No modules in {openArea.display_name} yet.
              {openArea.modules_status === "pending" && " We\u2019re still checking."}
            </p>
          )}

          {landingState === "ideal" && openArea && openModule && (
            <ModuleViewport
              title={openModule.display_name}
              areaName={openArea.display_name}
              decision={effectiveDecision(openModule.module_key, openModule.permission)}
              ownerName={ownerDisplayName(openArea.owner_email)}
              tabs={openModule.tabs}
              // A docked surface brings its own header; the shell's title and
              // freshness line are dropped so the frame is not preceded by a
              // duplicate name and an unknowable date (Damien, 2026-08-24).
              bare={openModule.kind === "docked"}
              // The S8 envelope's meta, handed up from the resolved native
              // module (registry HARD 11). Docked surfaces return no envelope
              // (bare drops the line entirely); an unregistered native key has
              // no data of its own to be fresh about.
              meta={openModule.kind === "docked" ? undefined : nativeMeta}
              activeTabKey={activeTabKey}
              tabCounts={openModule.kind === "docked" ? undefined : tabHints?.counts}
              onOpenTab={(tabKey) =>
                void navigate({
                  to: "/app/$area/$module/$tab",
                  params: { area: openArea.area_key, module: openModule.module_key, tab: tabKey },
                  // Filters are the module's, not the tab's: switching tabs keeps
                  // them, so a range set on Overview still holds on UK (KET-6592).
                  search: ((prev: Record<string, unknown>) => carryFilters(prev)) as never,
                })
              }
            >
              {/* A docked surface renders here under the same login (C11, R3).
                  A registered native module renders its own tab CONTENT ONLY —
                  never its own ModuleViewport (I-7) — resolved from the
                  registry (HARD 9, 10). An unregistered native key still shows
                  the honest placeholder: chrome resolved, no view for it yet. */}
              {openModule.kind === "docked" ? (
                <DockedModule
                  module={openModule}
                  tabKey={params.tab ?? defaultTabKey(openModule.tabs)}
                  at={at}
                  ownerName={ownerDisplayName(openArea.owner_email)}
                />
              ) : (
                (() => {
                  const NativeComponent = resolveNativeModule(openModule.module_key);
                  return NativeComponent ? (
                    <NativeComponent
                      decision={effectiveDecision(openModule.module_key, openModule.permission)}
                      activeTabKey={activeTabKey}
                      areaName={openArea.display_name}
                      ownerName={ownerDisplayName(openArea.owner_email)}
                    />
                  ) : (
                    <p className="text-ks-ink">This module has no view yet.</p>
                  );
                })()
              )}
            </ModuleViewport>
          )}
        </main>
      </div>
    </div>
  );
}

/** sessionStorage, or null where the browser refuses it (private mode, blocked data). */
function attemptStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
